import {
  FluidFormElementState,
  FluidOption,
  FluidPaginationState,
  FluidPaginatorTranslationOptions,
  FluidTheme,
  FluidThemeInterface,
  itemExists,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
} from '@stencil/core';

import {
  PAGINATOR_NO_DATA,
  PAGINATOR_OPTION_DOES_NOT_EXIST,
} from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { FluidEvent, Utils } from '../../utils/utils';
import {
  PaginatorNav,
  PaginatorPageSizeSelect,
  PaginatorRangeIndicator,
} from './components/fluid-paginator.components';
import {
  calculateMaxCurrentPage,
  calculateRangeFrom,
  calculateRangeTo,
  calculateTotalPages,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_RANGE_INDICATOR,
  getDefaultPageSizeOptions,
  getDefaultPaginationState,
} from './utils/fluid-paginator.utils';

/**
 * @displayName Paginator
 *
 * @contextData FLUID Paginator allows users to efficiently navigate large data sets (such as Table rows) by breaking them into smaller, more manageable pages.
 * <br/><br/>
 * When the user interacts with the Paginator, a "pageChanged" Event is emitted that can be used to update any associated data view.
 */
@Component({
  tag: 'fluid-paginator',
  styleUrl: 'fluid-paginator.scss',
  shadow: true,
})
export class FluidPaginator implements FluidThemeInterface, ComponentInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  private _itemsPerPageSelectRef: HTMLFluidSelectElement;

  /**
   * Array of FluidOptions used to populate the "items per page" dropdown.
   */
  private _pageSizeOptions: FluidOption[];

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidPaginatorElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidPaginatorTranslationOptions;
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Derived Paginator state.
   */
  @State() _paginationState: FluidPaginationState = {};

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Array of objects whose length determines the # of pages shown
   * in the Paginator.
   */
  @Prop({ mutable: true }) dataItems: object[];

  /**
   * Allows custom range indicator wording to be rendered
   */
  @Prop({ mutable: true }) rangeIndicatorTemplate: string =
    DEFAULT_RANGE_INDICATOR();

  /**
   * Allows custom items per page wording to be rendered
   */
  @Prop({ mutable: true }) itemsPerPageTemplate: string =
    DEFAULT_ITEMS_PER_PAGE;

  /**
   * The page shown as "selected" by default on first render.
   * NOTE: this number is a zero-based index (i.e. "0" is the first page).
   */
  @Prop({ mutable: true }) currentPage = 0;

  /**
   * Array of numbers to be shown as page size options in the
   * "items per page" dropdown menu.
   */
  @Prop({ mutable: true }) itemsPerPageOptions: number[] =
    getDefaultPageSizeOptions();

  /**
   * Sets the default number of items per page. Accepts a number
   * or the string 'total' to show all items.
   */
  @Prop({ mutable: true }) defaultPageSize: number | 'total';

  /**
   * If true, Paginator will display a "jump to first page" button
   * before the "previous page" navigation button.
   */
  @Prop({ mutable: true }) showFirstButton = false;

  /**
   * If true, Paginator will display a "jump to last page" button
   * after the "next page" navigation button.
   */
  @Prop({ mutable: true }) showLastButton = false;

  /**
   * When true, "total items" (last item of the select box) will not
   * be an option.
   */
  @Prop({ mutable: true }) isTotalItemsDisabled = false;

  /**
   * When supplied, will be prepended on each inner element of the paginator in order
   * to help facilitate testing.
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Events

  /**
   * Event fired when a new page is selected within Paginator.
   * Emits the derived FluidPaginationState object.
   */
  @Event() pageChanged: EventEmitter<FluidPaginationState>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback() {
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called before every render.
   * Updates the state and resets the items per page options
   */
  componentWillRender() {
    const isPaginationStateEmpty = Utils.isEmptyObject(this._paginationState);
    if (isPaginationStateEmpty && this.dataItems) {
      this.setPaginationState();
    }
    if (!isPaginationStateEmpty) {
      this.calculatePaginationState(false);
    }
    this.createPageSizeOptions(
      this.dataItems,
      this.itemsPerPageOptions,
      this._paginationState.itemsPerPage,
      this.isTotalItemsDisabled
    );
  }

  /**
   * Called every time the component is disconnected from the DOM.
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Go to the next page (if not already on the last page).
   */
  @Method() async goToNextPage() {
    this.nextPage();
  }

  /**
   * Go to the previous page (if not already on the first page).
   */
  @Method() async goToPreviousPage() {
    this.previousPage();
  }

  /**
   * Sets the Paginator's page to the given page number.
   * Note - page numbers are zero-indexed, where 0 is the first page.
   */
  @Method() async setPage(pageNumber: number) {
    this.goToPage(pageNumber);
    return pageNumber;
  }

  /**
   * Sets the page size to the given number, or 'total' to show all items.
   */
  @Method() async setItemsPerPage(itemsPerPage: number | 'total') {
    const pageSizes = this._pageSizeOptions.map((opt) =>
      parseInt(opt.value, 10)
    );

    if (itemsPerPage === 'total') {
      itemsPerPage = this.dataItems?.length;
    }
    if (!itemExists<number>(pageSizes, itemsPerPage)) {
      Utils.consoleWarn(
        this.host,
        PAGINATOR_OPTION_DOES_NOT_EXIST(itemsPerPage)
      );
      return -1;
    }
    await this._itemsPerPageSelectRef.setValue(itemsPerPage?.toString());
    this.handlePageSizeChange(FluidEvent({ value: itemsPerPage }));
    return itemsPerPage;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Remove any options greater than the length of the dataItems array,
   * and if it's not already there, add the length as a 'show all' type
   * option. All page sizes are converted to FluidOption objects so that
   * they're compatible with FLUID Select.
   */
  createPageSizeOptions(
    dataItems: object[],
    itemsPerPageOptions: number[],
    itemsPerPage: number,
    isTotalItemsDisabled: boolean
  ): void {
    // If there are no data items, don't create any page size options
    if (!dataItems?.length) {
      this._pageSizeOptions = [];
      return;
    }

    const totalItems = dataItems.length;

    // Filter out any options that are less than 0 or greater than the total number of items
    const pageSizes = itemsPerPageOptions.filter(
      (pageSize) => pageSize > 0 && pageSize <= totalItems
    );

    // Add current page size if it's not already in "itemsPerPageOptions"
    itemsPerPage < totalItems &&
      !itemsPerPageOptions.includes(itemsPerPage) &&
      pageSizes.push(itemsPerPage);

    // Add total as the last option
    !isTotalItemsDisabled && pageSizes.push(totalItems);

    // Remove duplicates, sort, and convert to FluidOption array
    this._pageSizeOptions = [...new Set(pageSizes)]
      .sort((a, b) => a - b)
      .map((pageSize) => ({
        label: `${pageSize}`,
        value: `${pageSize}`,
      }));
  }

  /**
   * Set initial pagination state from props.
   */
  setPaginationState(): void {
    let defaultPageSize: number;
    switch (true) {
      // If "defaultPageSize" is undefined or isn't a number, use 1st option in "itemsPerPageOptions"
      case !this.defaultPageSize:
      case !Utils.isNumber(this.defaultPageSize) &&
        this.defaultPageSize !== 'total':
        defaultPageSize = this.itemsPerPageOptions[0];
        break;
      // If "defaultPageSize" is 'total' or greater than total # of items, use "dataItems" total
      case this.defaultPageSize === 'total':
      case (this.defaultPageSize as number) > this.dataItems?.length:
        defaultPageSize = this.dataItems?.length;
        break;
      // Otherwise, use provided "defaultPageSize" value
      default:
        defaultPageSize = this.defaultPageSize as number;
    }
    this._paginationState = getDefaultPaginationState(
      this.dataItems,
      defaultPageSize,
      this.currentPage
    );
    this.pageChanged.emit(this._paginationState);
  }

  /**
   * If we're not on the last page, increase the current page number
   * and update pagination state.
   */
  nextPage(): void {
    const { currentPage, totalPages } = this._paginationState;
    if (currentPage + 1 <= totalPages - 1) {
      this._paginationState = {
        ...this._paginationState,
        currentPage: currentPage + 1,
      };
      this.calculatePaginationState();
    }
  }

  /**
   * If we're not on the first page, reduce the current page number
   * and update pagination state.
   */
  previousPage(): void {
    const { currentPage } = this._paginationState;
    if (currentPage - 1 >= 0) {
      this._paginationState = {
        ...this._paginationState,
        currentPage: currentPage - 1,
      };
      this.calculatePaginationState();
    }
  }

  /**
   * If we're not already on the specified page, set the current page
   * to the given pageNo and update pagination state.
   * @param pageNo
   */
  goToPage(pageNo: number) {
    if (this._paginationState.currentPage !== pageNo) {
      this._paginationState = { ...this._paginationState, currentPage: pageNo };
      this.calculatePaginationState();
    }
  }

  /**
   * Calculate each part of pagination state based on a single state change,
   * then emit to calling component to update whatever is being paginated.
   */
  calculatePaginationState(emit = true): void {
    const { currentPage, itemsPerPage } = this._paginationState;
    const newCurrentPage = calculateMaxCurrentPage(
      currentPage,
      calculateTotalPages(this.dataItems, itemsPerPage)
    );
    const newTotalPages = calculateTotalPages(this.dataItems, itemsPerPage);

    this._paginationState = {
      ...this._paginationState,
      currentPage: newCurrentPage,
      totalPages: newTotalPages,
      rangeFrom: calculateRangeFrom(newCurrentPage, itemsPerPage),
      rangeTo: calculateRangeTo(
        newCurrentPage,
        newTotalPages,
        this.dataItems,
        itemsPerPage
      ),
    };

    if (emit) {
      this.pageChanged.emit(this._paginationState);
    }
  }

  /**
   * Handle the selection of itemsPerPage - grab number from the event,
   * update pagination state, then call "calculate" to recalculate other elements.
   * @param event
   */
  handlePageSizeChange(event: CustomEvent<FluidFormElementState>): void {
    this._paginationState.itemsPerPage = parseInt(event.detail.value);
    this.calculatePaginationState();
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    if (!this.dataItems) {
      Utils.consoleWarn(this.host, PAGINATOR_NO_DATA);
    }
    return (
      this.dataItems && (
        <div
          class={`fluid-paginator theme-${this.theme}`}
          data-test-id={`${this.identifier}-fluid-paginator`}>
          <PaginatorRangeIndicator
            identifier={this.identifier}
            paginationState={this._paginationState}
            totalItems={this.dataItems.length}
            rangeIndicatorTemplate={this.rangeIndicatorTemplate}
            i18nStore={this._i18nStore}
            fluidTranslate={this.fluidTranslate}
          />
          <PaginatorNav
            identifier={this.identifier}
            paginationState={this._paginationState}
            showFirstButton={this.showFirstButton}
            showLastButton={this.showLastButton}
            goToPage={(pageNumber) => this.goToPage(pageNumber)}
            nextPage={() => this.nextPage()}
            previousPage={() => this.previousPage()}
            theme={this.theme}
            overrideGlobalTheme={this.overrideGlobalTheme}
            i18nStore={this._i18nStore}
            fluidTranslate={this.fluidTranslate}
          />
          <PaginatorPageSizeSelect
            identifier={this.identifier}
            itemsPerPageOptions={this._pageSizeOptions}
            setSelectRef={(ref) => (this._itemsPerPageSelectRef = ref)}
            handlePageSizeChange={(event) => this.handlePageSizeChange(event)}
            theme={this.theme}
            selectedPageSize={this._paginationState.itemsPerPage}
            overrideGlobalTheme={this.overrideGlobalTheme}
            itemsPerPageTemplate={this.itemsPerPageTemplate}
            i18nStore={this._i18nStore}
            fluidTranslate={this.fluidTranslate}
          />
        </div>
      )
    );
  }
}
